import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Table as TableComponent } from '@src/shared/ui/components/TableStatic';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { isNull, orderBy } from 'lodash';
import React, { useMemo } from 'react';

import configuration from './includes/configuration.json';

/**
 * DobRecords
 *
 * Data containing trusted and unstable records from EIMP.
 *
 * @param rows
 * @returns {Element}
 * @constructor
 */
export function DobRecords({ rows = [] }) {
  /**
   * renderCellIcon
   *
   * Displays an icon to distinguish between trusted and untrusted records.
   *
   * @returns {Element}
   */
  const renderCellIcon = ({ row }) => {
    if (row?.original?.status === 'trusted') {
      return <IconSymbol color="var(--abyss-colors-success1)" icon="check_circle" variant="outlined" />;
    }

    if (row?.original?.status === 'untrusted') {
      return <IconSymbol color="var(--abyss-colors-error1)" icon="error" variant="outlined" />;
    }

    return null;
  };

  /**
   * renderCellDobLastModified
   *
   * Displays the date of birth last modified in a particular date/time format.
   *
   * @param value
   * @returns {*|string}
   */
  const renderCellDobLastModified = ({ value }) => {
    if (isNull(value)) {
      return '';
    }

    return dayjs(value).format('MM/DD/YYYY, HH:mm:ss');
  };

  /**
   * renderCellDate
   *
   * Displays the date in a particular date format.
   *
   * @param value
   * @returns {*|string}
   */
  const renderCellDate = ({ value }) => {
    if (isNull(value)) {
      return '';
    }

    return dayjs(value).format('MM/DD/YYYY');
  };

  /**
   * Columns for the table.
   */
  const columns = useMemo(() => {
    return orderBy(configuration?.initialColumns, ['order'], ['asc'])?.map((column) => {
      const theColumn = column;

      if (theColumn?.accessor === 'status') {
        theColumn.Cell = renderCellIcon;
      }

      if (theColumn?.Header === 'DOB Last Modified') {
        theColumn.Cell = renderCellDobLastModified;
      }

      if (['Coverage End', 'Coverage Last Modified', 'HCM Patient Created'].includes(theColumn?.Header)) {
        theColumn.Cell = renderCellDate;
      }

      return theColumn;
    });
  }, []);

  return (
    <ErrorHandler location="src/widgets/RiskRecords/Eimp/components/DobRecords/DobRecords.jsx">
      <TableComponent
        {...{
          accessor: `eidSearch-eimp`,
          columns,
          configuration,
          rows,
        }}
      />
    </ErrorHandler>
  );
}
